// @ts-strict-ignore
type OnSeenCallback<Key> = (key: Key) => void;

export class ViewportSeenWatcher<Key> {
  private declare intersectionObserver: IntersectionObserver;
  private declare onSeenCallback: OnSeenCallback<Key>;
  private declare seenKeys: Set<Key>; // set of target keys that were visible at least once
  private declare waitUntilSeenResolvers: Map<Key, (value?: boolean | PromiseLike<boolean>) => void>;
  private declare targetLookup: Map<Key, Element>;
  private declare keyLookup: Map<Element, Key>;

  constructor(onSeenCallback?: OnSeenCallback<Key>) {
    this.onSeenCallback = onSeenCallback;
    this.intersectionObserver = new IntersectionObserver(this.handleObserverCallback.bind(this), {
      root: null, // defaults to browser viewport
      threshold: 0,
    });
    this.seenKeys = new Set<Key>();
    this.waitUntilSeenResolvers = new Map<Key, (value?: boolean | PromiseLike<boolean>) => void>();
    this.targetLookup = new Map<Key, Element>();
    this.keyLookup = new Map<Element, Key>();
  }

  public getAllSeenKeys() {
    return Array.from(this.seenKeys);
  }

  public wasSeen(key: Key): boolean {
    return this.seenKeys.has(key);
  }

  public watch(key: Key, target: Element) {
    this.unwatch(key);

    this.targetLookup.set(key, target);
    this.keyLookup.set(target, key);
    this.intersectionObserver.observe(target);
  }

  public unwatch(key: Key) {
    this.seenKeys.delete(key);
    if (this.targetLookup.has(key)) {
      const target = this.targetLookup.get(key);
      this.targetLookup.delete(key);
      this.keyLookup.delete(target);
      this.intersectionObserver.unobserve(target);
    }
  }

  public unwatchAll() {
    for (const key of this.targetLookup.keys()) {
      this.unwatch(key);
    }
  }

  // returns true when the watched target enters or has entered the viewport
  // returns false if wait is aborted with abortWait()
  public async waitUntilSeen(key: Key): Promise<boolean> {
    if (this.seenKeys.has(key)) {
      return true;
    }
    return new Promise<boolean>((resolve) => {
      this.waitUntilSeenResolvers.set(key, resolve);
    });
  }

  public abortAllWait() {
    const waiterKeys = Array.from(this.waitUntilSeenResolvers.keys());
    for (const key of waiterKeys) {
      const resolve = this.waitUntilSeenResolvers.get(key);
      resolve(false);
      this.waitUntilSeenResolvers.delete(key);
    }
  }

  public abortWait(key: Key) {
    if (this.waitUntilSeenResolvers.has(key)) {
      const resolve = this.waitUntilSeenResolvers.get(key);
      resolve(false);
      this.waitUntilSeenResolvers.delete(key);
    }
  }

  private handleObserverCallback(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const key = this.keyLookup.get(entry.target);
        this.seenKeys.add(key);
        this.onSeenCallback?.(key);
        if (this.waitUntilSeenResolvers.has(key)) {
          const resolve = this.waitUntilSeenResolvers.get(key);
          resolve(true);
          this.waitUntilSeenResolvers.delete(key);
        }
      }
    }
  }
}
